import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCustomers, deleteCustomer, getCustomerGroupsPaginated, deleteCustomerGroups } from "./api";
import type { IPaginated, ICustomer, IListCustomersParams, ICustomerGroup, IListCustomerGroupsParams, IPaginatedGroups } from "./types";

export const customersKeys = {
  root: ["customers"] as const,
  list: (params: Partial<IListCustomersParams>) =>
    [...customersKeys.root, "list", params] as const,
};

export function useCustomers(
  params: IListCustomersParams,
  /** Lets a consumer defer the request (e.g. the command palette, which
   *  should not fetch until the user actually types a query). */
  opts?: { enabled?: boolean }
) {
  return useQuery<IPaginated<ICustomer>>({
    queryKey: customersKeys.list(params ?? {}),
    queryFn: () => getCustomers(params),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteCustomer(id),

    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: customersKeys.root });

      const snapshots = qc.getQueriesData<IPaginated<ICustomer>>({
        queryKey: customersKeys.root,
      });

      snapshots.forEach(([key, prev]) => {
        if (!prev) return;
        const next: IPaginated<ICustomer> = {
          ...prev,
          data: prev.data.filter((c) => c.id !== Number(id)),
          total: Math.max(0, (prev.total ?? 0) - (prev.data.some(c => c.id === Number(id)) ? 1 : 0)),
        };
        qc.setQueryData(key, next);
      });

      return { snapshots };
    },

    onError: (_err, _id, ctx) => {
      ctx?.snapshots?.forEach(([key, prev]) => qc.setQueryData(key, prev));
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: customersKeys.root });
    },
  });
}


export function useDeleteCustomerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteCustomerGroups(id),

    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: customerGroupKeys.root });

      const snapshots = qc.getQueriesData<{ data: ICustomerGroup[];[k: string]: any }>({
        queryKey: customerGroupKeys.root,
      });

      snapshots.forEach(([key, prev]) => {
        if (!prev || !Array.isArray(prev.data)) return;
        qc.setQueryData(key, {
          ...prev,
          data: prev.data.filter((g) => g.id !== Number(id)),
          total: Math.max(0, (prev.total ?? 0) - (prev.data.some((g) => g.id === Number(id)) ? 1 : 0)),
        });
      });

      return { snapshots };
    },

    onError: (_err, _id, ctx) => {
      ctx?.snapshots?.forEach(([key, prev]) => qc.setQueryData(key, prev));
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: customerGroupKeys.root });
      qc.invalidateQueries({ queryKey: customersKeys.root });
    },
  });
}



export const customerGroupKeys = {
  root: ["customerGroups"] as const,
  all: () => [...customerGroupKeys.root, "all"] as const,
  list: (params: Partial<IListCustomerGroupsParams>) =>
    [...customerGroupKeys.root, "list", params] as const,
};

export function useCustomerGroups(params: IListCustomerGroupsParams = {}) {
  return useQuery<IPaginatedGroups>({
    queryKey: customerGroupKeys.list(params),
    queryFn: () => getCustomerGroupsPaginated(params),
    placeholderData: (prev) => prev,
    staleTime: 5 * 60 * 1000,
  });
}